import { useState, useEffect, useMemo, useRef } from 'react';
import { Box, Button, Typography, TextField, CircularProgress, Alert, LinearProgress, Link as MuiLink } from '@mui/material';
import { Users, ShieldCheck, ShieldPlus, CalendarDays, BarChart3 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { usePatientAuth } from '../../contexts/PatientAuthContext';
import { Link, useSearchParams } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { LoginRateLimiter } from '../../lib/loginSecurity';
import { RateLimitError } from '../../lib/api/client';
import {
  AuthScreenLayout, AuthEmailPassword, AuthSubmitButton, OrDivider, FieldLabel,
  alertSx, fieldSx, BRAND_GRADIENT, ACCENT_BLUE, SLATE, HEADING_NAVY, type Feature,
} from './authShared';

// Module-level singleton — persists across re-renders, resets on page reload
const rateLimiter = new LoginRateLimiter(5, 30_000);

// Icons are static; the copy is read from the `auth` namespace at render time so the panel
// re-renders in the new language the moment the switcher is used.
const FEATURE_KEYS = [
  { icon: CalendarDays, key: 'smartAppointments' },
  { icon: ShieldPlus,   key: 'patientManagement' },
  { icon: BarChart3,    key: 'insightsReports' },
  { icon: ShieldCheck,  key: 'secureCompliant' },
] as const;

// Shared by the three agreement links, which are now rendered by <Trans> rather than inline.
const legalLinkSx = { color: '#94a3b8', textDecoration: 'underline', fontWeight: 600 } as const;

export function Login() {
  const { t } = useTranslation('auth');
  const features: Feature[] = useMemo(
    () => FEATURE_KEYS.map(({ icon, key }) => ({
      icon,
      title: t(`feature.${key}`),
      desc: t(`feature.${key}Desc`),
    })),
    [t],
  );
  const { signIn: staffSignIn, completeMfaLogin } = useAuth();
  const { signIn: patientSignIn } = usePatientAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorSeverity, setErrorSeverity] = useState<'error' | 'warning'>('error');
  const [success, setSuccess] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [lockSecondsLeft, setLockSecondsLeft] = useState(0);
  const lockTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // MFA challenge step — shown instead of the email/password form once the password has been
  // verified for an account with two-factor authentication enabled.
  const [mfaPendingToken, setMfaPendingToken] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [mfaError, setMfaError] = useState<string | null>(null);
  const [mfaLoading, setMfaLoading] = useState(false);

  useEffect(() => {
    const invited = searchParams.get('invited');
    const invitedEmail = searchParams.get('email');
    if (invited === 'true') {
      setSuccess(t('success.accountSetUp'));
      if (invitedEmail) setEmail(decodeURIComponent(invitedEmail));
      setSearchParams({});
    }
  }, [searchParams, setSearchParams, t]);

  // Countdown timer when locked out
  useEffect(() => {
    if (lockSecondsLeft <= 0) return;
    lockTimerRef.current = setInterval(() => {
      const secs = rateLimiter.secondsRemaining();
      setLockSecondsLeft(secs);
      if (secs <= 0 && lockTimerRef.current) {
        clearInterval(lockTimerRef.current);
        lockTimerRef.current = null;
        setError(null);
      }
    }, 1000);
    return () => {
      if (lockTimerRef.current) clearInterval(lockTimerRef.current);
    };
  }, [lockSecondsLeft]);

  const applyRateLimit = (seconds: number) => {
    rateLimiter.forceLockFor(seconds);
    setLockSecondsLeft(seconds);
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (rateLimiter.isLocked()) {
      setLockSecondsLeft(rateLimiter.secondsRemaining());
      return;
    }

    const newErrors: Record<string, string> = {};
    if (!email) newErrors.email = t('error.emailRequired');
    if (!password) newErrors.password = t('error.passwordRequired');
    if (Object.keys(newErrors).length > 0) {
      setFieldErrors(newErrors);
      setError(t('error.fillRequiredFields'));
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);
    setFieldErrors({});

    try {
      const result = await staffSignIn(email, password);
      rateLimiter.recordSuccess();
      setPassword('');
      if (result.mfaRequired) {
        setMfaPendingToken(result.mfaPendingToken);
        setLoading(false);
        return;
      }
      return;
    } catch (staffErr) {
      // Server rate-limited this IP — no point trying patient login
      if (staffErr instanceof RateLimitError) {
        setPassword('');
        setLoading(false);
        applyRateLimit(staffErr.retryAfterSeconds);
        return;
      }

      // Account requires password upgrade — direct to reset, don't try patient login
      const errMsg = staffErr instanceof Error ? staffErr.message : String(staffErr);
      if (errMsg === 'ACCOUNT_UPGRADE_REQUIRED' || errMsg.includes('ACCOUNT_UPGRADE_REQUIRED')) {
        setPassword('');
        setLoading(false);
        setError(t('error.passwordResetRequired'));
        return;
      }

      // Staff login failed for another reason — try patient login
      try {
        await patientSignIn(email, password);
        rateLimiter.recordSuccess();
        setPassword('');
        window.location.href = '/patient/dashboard';
        return;
      } catch (patientErr) {
        setPassword('');

        if (patientErr instanceof RateLimitError) {
          setLoading(false);
          applyRateLimit(patientErr.retryAfterSeconds);
          return;
        }

        rateLimiter.recordFailure();

        if (rateLimiter.isLocked()) {
          const secs = rateLimiter.secondsRemaining();
          setLockSecondsLeft(secs);
          setError(null);
        } else {
          const remaining = rateLimiter.attemptsRemaining();
          // Severity is tracked in state rather than sniffed out of the message text. The old
          // `error.includes('attempt')` check silently stopped working the moment the message
          // was no longer English.
          setErrorSeverity(remaining <= 3 ? 'warning' : 'error');
          setError(
            remaining <= 3
              ? t('error.invalidCredentialsAttempts', { count: remaining })
              : t('error.invalidCredentials'),
          );
        }
        setLoading(false);
      }
    }
  };

  const handleMfaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mfaPendingToken || !mfaCode.trim()) return;

    setMfaLoading(true);
    setMfaError(null);
    try {
      await completeMfaLogin(
        mfaPendingToken,
        useBackupCode ? undefined : mfaCode.trim(),
        useBackupCode ? mfaCode.trim() : undefined,
      );
      // AuthContext state is now populated — the app's own routing takes over from here.
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      if (msg === 'MFA_SESSION_EXPIRED') {
        setMfaError(t('mfa.expired'));
        setMfaPendingToken(null);
        setMfaCode('');
      } else if (msg === 'MFA_LOCKED') {
        setMfaError(t('mfa.tooManyAttempts'));
      } else {
        setMfaError(t('mfa.incorrectCode'));
      }
      setMfaLoading(false);
    }
  };

  // ── MFA challenge — same background, a single centred card ──
  if (mfaPendingToken) {
    return (
      <Box
        sx={{
          minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
          backgroundColor: '#0b1c30',
          backgroundImage:
            'linear-gradient(105deg, rgba(9,20,36,0.90) 0%, rgba(9,20,36,0.55) 100%), url(/assets/images/login-background.png)',
          backgroundSize: 'cover', backgroundPosition: 'center', p: { xs: 2.5, md: 4 },
        }}
      >
        <Box
          sx={{
            width: '100%', maxWidth: 440, backgroundColor: '#fff', borderRadius: '24px',
            boxShadow: '0 30px 80px rgba(2,10,25,0.45)', p: { xs: 3.5, sm: 5 },
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Box
              sx={{
                width: 60, height: 60, mx: 'auto', mb: 1.5, borderRadius: '50%', display: 'grid',
                placeItems: 'center', color: ACCENT_BLUE,
                border: `2px solid ${ACCENT_BLUE}22`, boxShadow: `0 0 0 7px ${ACCENT_BLUE}0d`,
              }}
            >
              <ShieldCheck size={26} />
            </Box>
            <Typography sx={{ fontWeight: 800, fontSize: '1.5rem', color: HEADING_NAVY }}>
              {t('mfa.twoFactorTitle')}
            </Typography>
            <Typography sx={{ color: SLATE, mt: 0.75 }}>
              {useBackupCode
                ? t('mfa.backupHint')
                : t('mfa.authenticatorHint')}
            </Typography>
          </Box>

          <form onSubmit={handleMfaSubmit}>
            {mfaError && <Alert severity="error" sx={alertSx}>{mfaError}</Alert>}

            <FieldLabel>{useBackupCode ? t('field.backupCode') : t('field.verificationCode')}</FieldLabel>
            <TextField
              fullWidth autoFocus placeholder={useBackupCode ? t('placeholder.backupCode') : t('placeholder.sixDigitCode')}
              value={mfaCode} onChange={(e) => setMfaCode(e.target.value)} disabled={mfaLoading}
              inputProps={useBackupCode ? {} : { inputMode: 'numeric', maxLength: 6 }}
              sx={{ ...fieldSx, mb: 2.5 }}
            />

            <Button
              fullWidth type="submit" variant="contained" disableElevation
              disabled={mfaLoading || !mfaCode.trim()}
              sx={{
                py: 1.4, fontSize: '1rem', fontWeight: 700, borderRadius: '12px', textTransform: 'none',
                background: BRAND_GRADIENT, '&:hover': { background: BRAND_GRADIENT, filter: 'brightness(1.05)' },
              }}
            >
              {mfaLoading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : t('action.verify')}
            </Button>

            <Button
              fullWidth variant="text" size="small" sx={{ mt: 1.5, textTransform: 'none', fontWeight: 600 }}
              onClick={() => { setUseBackupCode(!useBackupCode); setMfaCode(''); setMfaError(null); }}
            >
              {useBackupCode ? t('action.useAuthenticatorCode') : t('action.useBackupCode')}
            </Button>
            <Button
              fullWidth variant="text" size="small" color="inherit"
              sx={{ mt: 0.5, textTransform: 'none', color: SLATE }}
              onClick={() => { setMfaPendingToken(null); setMfaCode(''); setMfaError(null); }}
            >
              {t('action.backToSignIn')}
            </Button>
          </form>
        </Box>
      </Box>
    );
  }

  const disabled = loading || lockSecondsLeft > 0;

  return (
    <AuthScreenLayout
      heading={{ pre: t('staff.headingPre'), accent: t('staff.headingAccent') }}
      subtext={t('staff.subtext')}
      features={features}
      cardTitle={t('staff.welcomeBack')}
      cardSubtitle={t('staff.title')}
    >
      <form onSubmit={handleSubmit}>
        {success && (
          <Alert severity="success" sx={alertSx} onClose={() => setSuccess(null)}>{success}</Alert>
        )}

        {lockSecondsLeft > 0 ? (
          <Alert
            severity="warning"
            sx={{ ...alertSx, alignItems: 'flex-start' }}
            action={
              <Box
                component={Link}
                to="/forgot-password"
                sx={{ color: 'warning.dark', fontWeight: 700, textDecoration: 'none', fontSize: '0.8rem', whiteSpace: 'nowrap' }}
              >
                {t('lock.resetPassword')}
              </Box>
            }
          >
            <Typography variant="body2" fontWeight={600}>{t('error.tooManyFailedAttempts')}</Typography>
            <Typography variant="body2">
              <Trans
                i18nKey="lock.retry"
                ns="auth"
                values={{ seconds: lockSecondsLeft }}
                components={{ b: <strong /> }}
              />
            </Typography>
            <LinearProgress
              variant="determinate"
              value={100 - (lockSecondsLeft / Math.max(rateLimiter.secondsRemaining() || lockSecondsLeft, 1)) * 100}
              color="warning"
              sx={{ mt: 1, borderRadius: 1, height: 4 }}
            />
          </Alert>
        ) : error ? (
          <Alert severity={errorSeverity} sx={alertSx}>{error}</Alert>
        ) : null}

        <AuthEmailPassword
          email={email}
          password={password}
          onEmail={(v) => { setEmail(v); setFieldErrors((p) => ({ ...p, email: '' })); }}
          onPassword={(v) => { setPassword(v); setFieldErrors((p) => ({ ...p, password: '' })); }}
          disabled={disabled}
          emailError={fieldErrors.email}
          passwordError={fieldErrors.password}
        />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Box component={Link} to="/forgot-password" sx={{ color: ACCENT_BLUE, fontWeight: 600, textDecoration: 'none', fontSize: '0.88rem' }}>
            {t('action.forgotPassword')}
          </Box>
        </Box>

        <AuthSubmitButton disabled={disabled}>
          {loading ? (
            <><CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />{t('action.signingIn')}</>
          ) : lockSecondsLeft > 0 ? (
            t('action.lockedRetry', { seconds: lockSecondsLeft })
          ) : (
            t('action.signIn')
          )}
        </AuthSubmitButton>
      </form>

      <OrDivider />

      {/*
        Patient entry point. This form already falls back to patient sign-in when staff auth fails,
        so a patient CAN log in here — but the dedicated route carries patient-specific copy and
        recovery, so surface it explicitly.
      */}
      <Button
        component={Link}
        to="/patient/login"
        fullWidth
        variant="outlined"
        startIcon={<Users size={18} />}
        sx={{
          py: 1.1, borderRadius: '12px', textTransform: 'none', fontWeight: 600, fontSize: '0.95rem',
          color: ACCENT_BLUE, borderColor: '#cbd5e1',
          '&:hover': { borderColor: ACCENT_BLUE, backgroundColor: `${ACCENT_BLUE}08` },
        }}
      >
        {t('staff.imAPatient')}
      </Button>

      <Typography sx={{ textAlign: 'center', mt: 1.75, color: '#94a3b8', fontSize: '0.78rem' }}>
        {t('staff.pharmacyTeam')}{' '}
        <MuiLink component={Link} to="/pharmacy/login" sx={{ color: '#94a3b8', textDecoration: 'underline', fontWeight: 600 }}>
          {t('staff.pharmacySignIn')}
        </MuiLink>
      </Typography>

      {/*
        Compact agreement line. The visual reference omits it, but the Terms / Privacy / Access
        Policy links are a deliberate DPDP-compliance requirement and must not be dropped — kept here
        small and unobtrusive so it fits the design without dominating it.
      */}
      <Typography sx={{ textAlign: 'center', mt: 1.25, color: '#94a3b8', fontSize: '0.72rem', lineHeight: 1.5 }}>
        {/*
          The three links are laid out by the sentence, not by the code: Telugu puts the
          case marker after the last item, so the order and the punctuation between them
          belong to the translation rather than to JSX.
        */}
        <Trans
          i18nKey="legal.agreement"
          ns="auth"
          components={{
            terms: <MuiLink component={Link} to="/terms" sx={legalLinkSx} />,
            privacy: <MuiLink component={Link} to="/terms#privacy" sx={legalLinkSx} />,
            access: <MuiLink component={Link} to="/terms#access" sx={legalLinkSx} />,
          }}
        />
      </Typography>
    </AuthScreenLayout>
  );
}
